/**
 * i18n 유틸리티 함수
 * 레거시 필드와 i18n 필드 간 변환, 번역 가져오기, 검증 등의 기능 제공
 */

import {
  LanguageCode,
  FALLBACK_LANGUAGES,
  isValidLanguageCode,
} from '../../constants/i18n/languages';
import {
  I18nFieldData,
  ContentData,
  ContentFormData,
  TranslationCompleteness,
  I18nValidationError,
  TranslationStatus,
} from '../../types/i18n/i18nTypes';
import { I18N_CONFIG, getFieldConfig, isRequiredLanguage } from '../../../config/i18nConfig';

// Series 타입 import (동적 import를 사용하여 순환 참조 방지)
type SeriesData = any;
type SeriesFormData = any;
type SeriesApiRequest = any;

/**
 * 레거시 필드를 i18n 필드로 변환하는 함수
 * 요구사항 5.1: title → i18n_title.ko, title_en → i18n_title.en 매핑 수행
 */
export const convertLegacyToI18n = (content: ContentData): ContentFormData => {
  const result: ContentFormData = {
    // 레거시 필드 복사
    title: content.title || '',
    title_en: content.title_en,
    synopsis: content.synopsis,
    synopsis_en: content.synopsis_en,
    seo_title: content.seo_title,
    seo_synopsis: content.seo_synopsis,
    seo_keyword: content.seo_keyword,

    // i18n 필드 생성 및 변환
    i18n_title: {
      ko: content.i18n_title?.ko || content.title || '',
      en: content.i18n_title?.en || content.title_en,
      'zh-hans': content.i18n_title?.['zh-hans'],
      'zh-hant': content.i18n_title?.['zh-hant'],
    },
    i18n_synopsis: {
      ko: content.i18n_synopsis?.ko || content.synopsis,
      en: content.i18n_synopsis?.en || content.synopsis_en,
      'zh-hans': content.i18n_synopsis?.['zh-hans'],
      'zh-hant': content.i18n_synopsis?.['zh-hant'],
    },
    i18n_seo_title: {
      ko: content.i18n_seo_title?.ko || content.seo_title,
      en: content.i18n_seo_title?.en,
      'zh-hans': content.i18n_seo_title?.['zh-hans'],
      'zh-hant': content.i18n_seo_title?.['zh-hant'],
    },
    i18n_seo_synopsis: {
      ko: content.i18n_seo_synopsis?.ko || content.seo_synopsis,
      en: content.i18n_seo_synopsis?.en,
      'zh-hans': content.i18n_seo_synopsis?.['zh-hans'],
      'zh-hant': content.i18n_seo_synopsis?.['zh-hant'],
    },
    i18n_seo_keyword: {
      ko: content.i18n_seo_keyword?.ko || content.seo_keyword,
      en: content.i18n_seo_keyword?.en,
      'zh-hans': content.i18n_seo_keyword?.['zh-hans'],
      'zh-hant': content.i18n_seo_keyword?.['zh-hant'],
    },
  };

  return result;
};

/**
 * i18n 필드를 레거시 필드로 변환하는 함수
 * 요구사항 5.2: i18n_title.ko → title, i18n_title.en → title_en 매핑 수행
 */
export const convertI18nToLegacy = (content: ContentFormData): ContentData => {
  const result: ContentData = {
    // 기본 필드들 (실제 구현에서는 더 많은 필드가 있을 수 있음)
    id: 0, // 새 콘텐츠의 경우 0, 기존 콘텐츠는 실제 ID 사용
    slug: '',
    status: 'draft',
    created: new Date().toISOString(),
    modified: new Date().toISOString(),

    // 레거시 필드로 변환
    title: content.i18n_title?.ko || content.title || '',
    title_en: content.i18n_title?.en || content.title_en,
    synopsis: content.i18n_synopsis?.ko || content.synopsis,
    synopsis_en: content.i18n_synopsis?.en || content.synopsis_en,
    seo_title: content.i18n_seo_title?.ko || content.seo_title,
    seo_synopsis: content.i18n_seo_synopsis?.ko || content.seo_synopsis,
    seo_keyword: content.i18n_seo_keyword?.ko || content.seo_keyword,

    // i18n 필드 복사
    i18n_title: content.i18n_title,
    i18n_synopsis: content.i18n_synopsis,
    i18n_seo_title: content.i18n_seo_title,
    i18n_seo_synopsis: content.i18n_seo_synopsis,
    i18n_seo_keyword: content.i18n_seo_keyword,
  };

  return result;
};

/**
 * 특정 언어의 번역을 가져오는 함수 (폴백 지원)
 * 요구사항 5.3: 요청된 언어가 없으면 폴백 언어(기본값: ko) 사용
 */
export const getTranslation = (
  i18nField: I18nFieldData | undefined,
  language: LanguageCode,
  fallbackLanguages: LanguageCode[] = FALLBACK_LANGUAGES
): string => {
  if (!i18nField) return '';

  // 요청된 언어의 번역이 있으면 반환
  if (i18nField[language]) {
    return i18nField[language] || '';
  }

  // 폴백 언어 순서대로 확인
  for (const fallbackLang of fallbackLanguages) {
    if (i18nField[fallbackLang]) {
      return i18nField[fallbackLang] || '';
    }
  }

  return '';
};

/**
 * SEO 필드 전용 번역 가져오기 함수 (폴백 없음)
 * 요구사항 9.4: SEO 필드는 폴백하지 않고 해당 언어의 값만 반환
 */
export const getSeoTranslation = (
  seoField: I18nFieldData | undefined,
  language: LanguageCode
): string => {
  if (!seoField) return '';
  return seoField[language] || '';
};

/**
 * 번역 완성도를 계산하는 함수
 * 요구사항 5.4: 필수 필드와 언어에 대한 완성 비율을 퍼센트로 반환
 */
export const calculateTranslationCompleteness = (
  content: ContentFormData,
  language: LanguageCode
): TranslationCompleteness => {
  const fieldNames = ['title', 'synopsis', 'seo_title', 'seo_synopsis', 'seo_keyword'];
  const i18nFieldNames = [
    'i18n_title',
    'i18n_synopsis',
    'i18n_seo_title',
    'i18n_seo_synopsis',
    'i18n_seo_keyword',
  ];

  let completedFields = 0;
  let totalFields = 0;

  // 각 필드에 대해 번역 완성도 확인
  i18nFieldNames.forEach((fieldName, index) => {
    const _fieldConfig = getFieldConfig(fieldNames[index]); // Task 7에서 사용 예정
    const i18nField = content[fieldName as keyof ContentFormData] as I18nFieldData;

    // 필수 언어이거나 해당 언어가 지원되는 경우만 카운트
    if (isRequiredLanguage(language) || I18N_CONFIG.supportedLanguages.includes(language)) {
      totalFields++;

      // 해당 언어의 번역이 있고 비어있지 않으면 완성된 것으로 간주
      if (i18nField && i18nField[language] && i18nField[language]!.trim() !== '') {
        completedFields++;
      }
    }
  });

  const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;

  return {
    language,
    completedFields,
    totalFields,
    percentage,
  };
};

/**
 * 모든 지원 언어에 대한 번역 완성도를 계산하는 함수
 */
export const calculateAllTranslationCompleteness = (
  content: ContentFormData
): TranslationCompleteness[] => {
  return I18N_CONFIG.supportedLanguages.map(language =>
    calculateTranslationCompleteness(content, language)
  );
};

/**
 * i18n 필드 검증 함수
 * 요구사항 5.5: 필수 언어(기본값: ko)에 대한 누락된 번역을 감지하고 오류 메시지 제공
 */
export const validateI18nFields = (content: ContentFormData): I18nValidationError[] => {
  const errors: I18nValidationError[] = [];
  const fieldNames = ['title', 'synopsis', 'seo_title', 'seo_synopsis', 'seo_keyword'];
  const i18nFieldNames = [
    'i18n_title',
    'i18n_synopsis',
    'i18n_seo_title',
    'i18n_seo_synopsis',
    'i18n_seo_keyword',
  ];

  // 각 필드에 대해 검증 수행
  i18nFieldNames.forEach((fieldName, index) => {
    const baseFieldName = fieldNames[index];
    const fieldConfig = getFieldConfig(baseFieldName);
    const i18nField = content[fieldName as keyof ContentFormData] as I18nFieldData;

    // 필수 언어에 대한 검증
    I18N_CONFIG.requiredLanguages.forEach(language => {
      const translation = i18nField?.[language];

      // 필수 필드이고 필수 언어인 경우 번역이 있어야 함
      if (fieldConfig.required && (!translation || translation.trim() === '')) {
        errors.push({
          field: fieldName,
          language,
          message: `${language} translation for ${baseFieldName} field is required`,
        });
      }

      // 최대 길이 검증
      if (translation && translation.length > fieldConfig.maxLength) {
        errors.push({
          field: fieldName,
          language,
          message: `${baseFieldName} field allows a maximum of ${fieldConfig.maxLength} characters`,
        });
      }
    });

    // 지원 언어에 대한 길이 검증 (필수가 아닌 언어도 포함)
    I18N_CONFIG.supportedLanguages.forEach(language => {
      const translation = i18nField?.[language];

      if (translation && translation.length > fieldConfig.maxLength) {
        errors.push({
          field: fieldName,
          language,
          message: `${baseFieldName} field allows a maximum of ${fieldConfig.maxLength} characters`,
        });
      }
    });
  });

  return errors;
};

/**
 * 실제 번역 데이터가 있는 언어 목록을 가져오는 함수
 * 요구사항 5.6: 실제 번역 데이터가 있는 언어만 반환
 */
export const getAvailableLanguages = (content: ContentFormData): LanguageCode[] => {
  const availableLanguages = new Set<LanguageCode>();
  const i18nFieldNames = [
    'i18n_title',
    'i18n_synopsis',
    'i18n_seo_title',
    'i18n_seo_synopsis',
    'i18n_seo_keyword',
  ];

  // 각 i18n 필드를 확인하여 번역이 있는 언어 수집
  i18nFieldNames.forEach(fieldName => {
    const i18nField = content[fieldName as keyof ContentFormData] as I18nFieldData;

    if (i18nField) {
      Object.entries(i18nField).forEach(([language, translation]) => {
        if (translation && translation.trim() !== '' && isValidLanguageCode(language)) {
          availableLanguages.add(language as LanguageCode);
        }
      });
    }
  });

  // 지원되는 언어만 필터링하여 반환
  return Array.from(availableLanguages).filter(lang =>
    I18N_CONFIG.supportedLanguages.includes(lang)
  );
};

/**
 * 콘텐츠의 번역 상태를 가져오는 함수
 */
export const getTranslationStatus = (content: ContentFormData): TranslationStatus => {
  const status: TranslationStatus = {};
  const fieldNames = ['title', 'synopsis', 'seo_title', 'seo_synopsis', 'seo_keyword'];
  const i18nFieldNames = [
    'i18n_title',
    'i18n_synopsis',
    'i18n_seo_title',
    'i18n_seo_synopsis',
    'i18n_seo_keyword',
  ];

  i18nFieldNames.forEach((fieldName, index) => {
    const baseFieldName = fieldNames[index];
    const i18nField = content[fieldName as keyof ContentFormData] as I18nFieldData;

    status[baseFieldName] = {};

    I18N_CONFIG.supportedLanguages.forEach(language => {
      const translation = i18nField?.[language];
      status[baseFieldName][language] = !!(translation && translation.trim() !== '');
    });
  });

  return status;
};

/**
 * 빈 i18n 필드 데이터를 생성하는 함수
 */
export const createEmptyI18nField = (): I18nFieldData => {
  const emptyField: I18nFieldData = {};

  I18N_CONFIG.supportedLanguages.forEach(language => {
    emptyField[language] = '';
  });

  return emptyField;
};

/**
 * i18n 필드가 비어있는지 확인하는 함수
 */
export const isI18nFieldEmpty = (i18nField: I18nFieldData | undefined): boolean => {
  if (!i18nField) return true;

  return Object.values(i18nField).every(translation => !translation || translation.trim() === '');
};

/**
 * 두 i18n 필드가 동일한지 비교하는 함수
 */
export const compareI18nFields = (
  field1: I18nFieldData | undefined,
  field2: I18nFieldData | undefined
): boolean => {
  if (!field1 && !field2) return true;
  if (!field1 || !field2) return false;

  return I18N_CONFIG.supportedLanguages.every(
    language => (field1[language] || '') === (field2[language] || '')
  );
};

/**
 * i18n 필드를 복사하는 함수 (깊은 복사)
 */
export const cloneI18nField = (i18nField: I18nFieldData | undefined): I18nFieldData => {
  if (!i18nField) return createEmptyI18nField();

  const cloned: I18nFieldData = {};
  Object.entries(i18nField).forEach(([language, translation]) => {
    if (
      isValidLanguageCode(language) &&
      I18N_CONFIG.supportedLanguages.includes(language as LanguageCode)
    ) {
      cloned[language as LanguageCode] = translation;
    }
  });

  return cloned;
};

/**
 * SEO 필드와 레거시 필드 간 동기화 함수
 * 요구사항 9.4, 9.6: SEO 필드 변경 시 레거시 필드 자동 업데이트
 */
export const syncSeoFieldsToLegacy = (content: ContentFormData): ContentFormData => {
  return {
    ...content,
    // SEO i18n 필드에서 레거시 필드로 동기화 (한국어 우선)
    seo_title: content.i18n_seo_title?.ko || content.seo_title,
    seo_synopsis: content.i18n_seo_synopsis?.ko || content.seo_synopsis,
    seo_keyword: content.i18n_seo_keyword?.ko || content.seo_keyword,
  };
};

/**
 * 레거시 SEO 필드에서 i18n 필드로 동기화 함수
 * 요구사항 9.4, 9.6: 레거시 SEO 필드 변경 시 i18n 필드 동기화
 */
export const syncLegacySeoFieldsToI18n = (content: ContentFormData): ContentFormData => {
  return {
    ...content,
    // 레거시 SEO 필드에서 i18n 필드로 동기화 (한국어로 설정)
    i18n_seo_title: {
      ...content.i18n_seo_title,
      ko: content.seo_title || content.i18n_seo_title?.ko,
    },
    i18n_seo_synopsis: {
      ...content.i18n_seo_synopsis,
      ko: content.seo_synopsis || content.i18n_seo_synopsis?.ko,
    },
    i18n_seo_keyword: {
      ...content.i18n_seo_keyword,
      ko: content.seo_keyword || content.i18n_seo_keyword?.ko,
    },
  };
};

/**
 * SEO 필드 변환 로직 - 레거시에서 i18n으로
 * 요구사항 9.4: SEO 필드 변환 로직 추가
 */
export const convertLegacySeoToI18n = (
  seoTitle?: string,
  seoSynopsis?: string,
  seoKeyword?: string
): {
  i18n_seo_title: I18nFieldData;
  i18n_seo_synopsis: I18nFieldData;
  i18n_seo_keyword: I18nFieldData;
} => {
  return {
    i18n_seo_title: {
      ko: seoTitle,
      en: undefined,
      'zh-hans': undefined,
      'zh-hant': undefined,
    },
    i18n_seo_synopsis: {
      ko: seoSynopsis,
      en: undefined,
      'zh-hans': undefined,
      'zh-hant': undefined,
    },
    i18n_seo_keyword: {
      ko: seoKeyword,
      en: undefined,
      'zh-hans': undefined,
      'zh-hant': undefined,
    },
  };
};

/**
 * SEO 필드 변환 로직 - i18n에서 레거시로
 * 요구사항 9.4: SEO 필드 변환 로직 추가
 */
export const convertI18nSeoToLegacy = (
  i18nSeoTitle?: I18nFieldData,
  i18nSeoSynopsis?: I18nFieldData,
  i18nSeoKeyword?: I18nFieldData
): {
  seo_title?: string;
  seo_synopsis?: string;
  seo_keyword?: string;
} => {
  return {
    seo_title: i18nSeoTitle?.ko,
    seo_synopsis: i18nSeoSynopsis?.ko,
    seo_keyword: i18nSeoKeyword?.ko,
  };
};

/**
 * I18nService 클래스 (레거시 호환용)
 * 기존 테스트와 컴포넌트에서 사용하는 클래스 기반 인터페이스
 */
export class I18nService {
  /**
   * 빈 i18n 데이터 생성
   */
  static createEmptyI18nData() {
    return {
      title: createEmptyI18nField(),
      synopsis: createEmptyI18nField(),
      seo_title: createEmptyI18nField(),
      seo_synopsis: createEmptyI18nField(),
      seo_keyword: createEmptyI18nField(),
    };
  }

  /**
   * API 응답을 안전하게 정규화
   */
  static safeNormalizeApiResponse(data: any) {
    return {
      title: data.title || '',
      title_en: data.title_en || '',
      synopsis: data.synopsis || '',
      synopsis_en: data.synopsis_en || '',
      i18n: {
        title: data.i18n_title || createEmptyI18nField(),
        synopsis: data.i18n_synopsis || createEmptyI18nField(),
        seo_title: data.i18n_seo_title || createEmptyI18nField(),
        seo_synopsis: data.i18n_seo_synopsis || createEmptyI18nField(),
        seo_keyword: data.i18n_seo_keyword || createEmptyI18nField(),
      },
    };
  }

  /**
   * 레거시 필드 변경 동기화
   */
  static syncLegacyFieldChange(formData: any, fieldName: string, value: string) {
    const updatedFormData = { ...formData, [fieldName]: value };

    // i18n 필드와 동기화
    if (fieldName === 'title') {
      updatedFormData.i18n = {
        ...formData.i18n,
        title: { ...formData.i18n?.title, ko: value },
      };
    } else if (fieldName === 'title_en') {
      updatedFormData.i18n = {
        ...formData.i18n,
        title: { ...formData.i18n?.title, en: value },
      };
    } else if (fieldName === 'synopsis') {
      updatedFormData.i18n = {
        ...formData.i18n,
        synopsis: { ...formData.i18n?.synopsis, ko: value },
      };
    } else if (fieldName === 'synopsis_en') {
      updatedFormData.i18n = {
        ...formData.i18n,
        synopsis: { ...formData.i18n?.synopsis, en: value },
      };
    } else if (fieldName === 'seo_title') {
      updatedFormData.i18n = {
        ...formData.i18n,
        seo_title: { ...formData.i18n?.seo_title, ko: value },
      };
    } else if (fieldName === 'seo_synopsis') {
      updatedFormData.i18n = {
        ...formData.i18n,
        seo_synopsis: { ...formData.i18n?.seo_synopsis, ko: value },
      };
    } else if (fieldName === 'seo_keyword') {
      updatedFormData.i18n = {
        ...formData.i18n,
        seo_keyword: { ...formData.i18n?.seo_keyword, ko: value },
      };
    }

    return updatedFormData;
  }

  /**
   * i18n과 레거시 데이터를 혼합 처리
   */
  static processMixedFormatData(data: any) {
    return {
      ...data,
      // 레거시 필드 우선 사용
      title: data.title || data.i18n?.title?.ko || '',
      title_en: data.title_en || data.i18n?.title?.en || '',
      synopsis: data.synopsis || data.i18n?.synopsis?.ko || '',
      synopsis_en: data.synopsis_en || data.i18n?.synopsis?.en || '',
      seo_title: data.seo_title || data.i18n?.seo_title?.ko || '',
      seo_synopsis: data.seo_synopsis || data.i18n?.seo_synopsis?.ko || '',
      seo_keyword: data.seo_keyword || data.i18n?.seo_keyword?.ko || '',
    };
  }

  /**
   * API 요청 데이터 생성
   */
  static createApiRequest(formData: any) {
    return {
      title: formData.title,
      title_en: formData.title_en,
      synopsis: formData.synopsis,
      synopsis_en: formData.synopsis_en,
      seo_title: formData.seo_title,
      seo_synopsis: formData.seo_synopsis,
      seo_keyword: formData.seo_keyword,
      i18n_title: formData.i18n?.title,
      i18n_synopsis: formData.i18n?.synopsis,
      i18n_seo_title: formData.i18n?.seo_title,
      i18n_seo_synopsis: formData.i18n?.seo_synopsis,
      i18n_seo_keyword: formData.i18n?.seo_keyword,
    };
  }

  /**
   * 레거시 → i18n 변환 (클래스 메서드)
   */
  static convertLegacyToI18n = convertLegacyToI18n;

  /**
   * i18n → 레거시 변환 (클래스 메서드)
   */
  static convertI18nToLegacy = convertI18nToLegacy;

  /**
   * SEO 번역 가져오기 (폴백 없음)
   */
  static getSeoTranslation = getSeoTranslation;

  /**
   * SEO 필드 동기화 함수들
   */
  static syncSeoFieldsToLegacy = syncSeoFieldsToLegacy;
  static syncLegacySeoFieldsToI18n = syncLegacySeoFieldsToI18n;
  static convertLegacySeoToI18n = convertLegacySeoToI18n;
  static convertI18nSeoToLegacy = convertI18nSeoToLegacy;
}
/**
 * Series 모델을 위한 i18n 헬퍼 함수들
 * 요구사항 10.4, 10.5, 10.6: Series 레거시 필드와 i18n 필드 간 실시간 동기화
 */

/**
 * 레거시 Series 필드를 i18n 필드로 변환하는 함수
 */
export const convertSeriesLegacyToI18n = (series: SeriesData): SeriesFormData => {
  const result: SeriesFormData = {
    // 기본 필드들 복사
    id: series.id,
    slug: series.slug,
    status: series.status,
    dubbing_mode: series.dubbing_mode,
    release_date_start: series.release_date_start,
    release_date_end: series.release_date_end,
    categories: series.categories,
    cast: series.cast,
    age_rating: series.age_rating,
    produce_country: series.produce_country,
    seasons: series.seasons,

    // 레거시 필드 복사
    title: series.title || '',
    title_en: series.title_en,
    synopsis: series.synopsis,
    synopsis_en: series.synopsis_en,
    seo_title: series.seo_title,
    seo_synopsis: series.seo_synopsis,
    seo_keyword: series.seo_keyword,

    // i18n 필드 생성 및 변환
    i18n_title: {
      ko: series.i18n_title?.ko || series.title || '',
      en: series.i18n_title?.en || series.title_en,
      'zh-hans': series.i18n_title?.['zh-hans'],
      'zh-hant': series.i18n_title?.['zh-hant'],
    },
    i18n_synopsis: {
      ko: series.i18n_synopsis?.ko || series.synopsis,
      en: series.i18n_synopsis?.en || series.synopsis_en,
      'zh-hans': series.i18n_synopsis?.['zh-hans'],
      'zh-hant': series.i18n_synopsis?.['zh-hant'],
    },
    i18n_seo_title: {
      ko: series.i18n_seo_title?.ko || series.seo_title,
      en: series.i18n_seo_title?.en,
      'zh-hans': series.i18n_seo_title?.['zh-hans'],
      'zh-hant': series.i18n_seo_title?.['zh-hant'],
    },
    i18n_seo_synopsis: {
      ko: series.i18n_seo_synopsis?.ko || series.seo_synopsis,
      en: series.i18n_seo_synopsis?.en,
      'zh-hans': series.i18n_seo_synopsis?.['zh-hans'],
      'zh-hant': series.i18n_seo_synopsis?.['zh-hant'],
    },
    i18n_seo_keyword: {
      ko: series.i18n_seo_keyword?.ko || series.seo_keyword,
      en: series.i18n_seo_keyword?.en,
      'zh-hans': series.i18n_seo_keyword?.['zh-hans'],
      'zh-hant': series.i18n_seo_keyword?.['zh-hant'],
    },
  };

  return result;
};

/**
 * i18n Series 필드를 레거시 필드로 변환하는 함수
 */
export const convertSeriesI18nToLegacy = (series: SeriesFormData): SeriesData => {
  const result: SeriesData = {
    // 기본 필드들
    id: series.id || 0,
    slug: series.slug || '',
    status: series.status || 'draft',
    created: new Date().toISOString(),
    modified: new Date().toISOString(),
    dubbing_mode: series.dubbing_mode,
    release_date_start: series.release_date_start,
    release_date_end: series.release_date_end,
    categories: series.categories,
    cast: series.cast,
    age_rating: series.age_rating,
    produce_country: series.produce_country,
    seasons: series.seasons,

    // 레거시 필드로 변환
    title: series.i18n_title?.ko || series.title || '',
    title_en: series.i18n_title?.en || series.title_en,
    synopsis: series.i18n_synopsis?.ko || series.synopsis,
    synopsis_en: series.i18n_synopsis?.en || series.synopsis_en,
    seo_title: series.i18n_seo_title?.ko || series.seo_title,
    seo_synopsis: series.i18n_seo_synopsis?.ko || series.seo_synopsis,
    seo_keyword: series.i18n_seo_keyword?.ko || series.seo_keyword,

    // i18n 필드 복사
    i18n_title: series.i18n_title,
    i18n_synopsis: series.i18n_synopsis,
    i18n_seo_title: series.i18n_seo_title,
    i18n_seo_synopsis: series.i18n_seo_synopsis,
    i18n_seo_keyword: series.i18n_seo_keyword,
  };

  return result;
};

/**
 * Series API 요청 데이터 생성 함수
 */
export const createSeriesApiRequest = (series: SeriesFormData): SeriesApiRequest => {
  return {
    // 기본 필드들
    slug: series.slug,
    status: series.status,
    dubbing_mode: series.dubbing_mode,
    release_date_start: series.release_date_start,
    release_date_end: series.release_date_end,
    categories: series.categories,
    cast: series.cast,
    age_rating: series.age_rating,
    produce_country: series.produce_country,

    // 레거시 필드
    title: series.title,
    title_en: series.title_en,
    synopsis: series.synopsis,
    synopsis_en: series.synopsis_en,
    seo_title: series.seo_title,
    seo_synopsis: series.seo_synopsis,
    seo_keyword: series.seo_keyword,

    // i18n 필드
    i18n_title: series.i18n_title,
    i18n_synopsis: series.i18n_synopsis,
    i18n_seo_title: series.i18n_seo_title,
    i18n_seo_synopsis: series.i18n_seo_synopsis,
    i18n_seo_keyword: series.i18n_seo_keyword,
  };
};

/**
 * Series 레거시 필드와 i18n 필드 간 실시간 동기화 함수
 */
export const syncSeriesFieldsToLegacy = (series: SeriesFormData): SeriesFormData => {
  return {
    ...series,
    // i18n 필드에서 레거시 필드로 동기화
    title: series.i18n_title?.ko || series.title || '',
    title_en: series.i18n_title?.en || series.title_en,
    synopsis: series.i18n_synopsis?.ko || series.synopsis,
    synopsis_en: series.i18n_synopsis?.en || series.synopsis_en,
    seo_title: series.i18n_seo_title?.ko || series.seo_title,
    seo_synopsis: series.i18n_seo_synopsis?.ko || series.seo_synopsis,
    seo_keyword: series.i18n_seo_keyword?.ko || series.seo_keyword,
  };
};

/**
 * Series 레거시 필드에서 i18n 필드로 동기화 함수
 */
export const syncSeriesLegacyFieldsToI18n = (series: SeriesFormData): SeriesFormData => {
  return {
    ...series,
    // 레거시 필드에서 i18n 필드로 동기화 (한국어로 설정)
    i18n_title: {
      ...series.i18n_title,
      ko: series.title || series.i18n_title?.ko,
      en: series.title_en || series.i18n_title?.en,
    },
    i18n_synopsis: {
      ...series.i18n_synopsis,
      ko: series.synopsis || series.i18n_synopsis?.ko,
      en: series.synopsis_en || series.i18n_synopsis?.en,
    },
    i18n_seo_title: {
      ...series.i18n_seo_title,
      ko: series.seo_title || series.i18n_seo_title?.ko,
    },
    i18n_seo_synopsis: {
      ...series.i18n_seo_synopsis,
      ko: series.seo_synopsis || series.i18n_seo_synopsis?.ko,
    },
    i18n_seo_keyword: {
      ...series.i18n_seo_keyword,
      ko: series.seo_keyword || series.i18n_seo_keyword?.ko,
    },
  };
};

/**
 * Series 필수 필드 검증 함수 (한국어 title, synopsis)
 */
export const validateSeriesRequiredFields = (series: SeriesFormData): I18nValidationError[] => {
  const errors: I18nValidationError[] = [];

  // 한국어 제목 필수 검증
  if (!series.i18n_title?.ko && !series.title) {
    errors.push({
      field: 'title',
      language: 'ko',
      message: 'Korean title is required',
      code: 'REQUIRED_KOREAN_TITLE',
    });
  }

  // 한국어 시놉시스 필수 검증 (요구사항에 따라 필수일 수 있음)
  if (!series.i18n_synopsis?.ko && !series.synopsis) {
    errors.push({
      field: 'synopsis',
      language: 'ko',
      message: 'Korean synopsis is required',
      code: 'REQUIRED_KOREAN_SYNOPSIS',
    });
  }

  return errors;
};

/**
 * Series 번역 완성도 계산 함수
 */
export const calculateSeriesTranslationCompleteness = (
  series: SeriesFormData,
  language: LanguageCode
): TranslationCompleteness => {
  const fields = ['title', 'synopsis', 'seo_title', 'seo_synopsis', 'seo_keyword'];
  let completedFields = 0;
  const totalFields = fields.length;

  fields.forEach(field => {
    const i18nFieldName = `i18n_${field}` as keyof SeriesFormData;
    const i18nField = series[i18nFieldName] as I18nFieldData;

    if (i18nField && i18nField[language]) {
      completedFields++;
    }
  });

  return {
    language,
    completedFields,
    totalFields,
    percentage: Math.round((completedFields / totalFields) * 100),
  };
};

/**
 * Series 지원 언어 목록 가져오기 함수
 */
export const getSeriesSupportedLanguages = (series: SeriesFormData): LanguageCode[] => {
  const supportedLanguages: Set<LanguageCode> = new Set();

  // 각 i18n 필드에서 실제 번역 데이터가 있는 언어 확인
  const i18nFields = [
    series.i18n_title,
    series.i18n_synopsis,
    series.i18n_seo_title,
    series.i18n_seo_synopsis,
    series.i18n_seo_keyword,
  ];

  i18nFields.forEach(field => {
    if (field) {
      Object.keys(field).forEach(lang => {
        if (field[lang as LanguageCode] && isValidLanguageCode(lang)) {
          supportedLanguages.add(lang as LanguageCode);
        }
      });
    }
  });

  return Array.from(supportedLanguages);
};

/**
 * Cast API 요청 데이터 생성 함수
 */
export const createCastApiRequest = (cast: CastFormData): CastApiRequest => {
  const request: CastApiRequest = {
    // 기본 필드들
    status: cast.status,

    // 레거시 필드
    name: cast.name,
    name_en: cast.name_en,

    // i18n 필드
    i18n_name: cast.i18n_name,

    // 기타 People 필드들
    birth_date: cast.birth_date,
    nationality: cast.nationality,
    type: cast.type,
    slug: cast.slug,
  };

  // thumbnail이 null이 아니거나 undefined가 아닐 때만 추가
  if (cast.thumbnail !== null && cast.thumbnail !== undefined) {
    request.thumbnail = cast.thumbnail;
  }

  return request;
};

/**
 * Cast 필수 필드 검증 함수 (영어 name)
 */
export const validateCastRequiredFields = (cast: CastFormData): I18nValidationError[] => {
  const errors: I18nValidationError[] = [];

  // 영어 이름 필수 검증
  if (!cast.i18n_name?.en && !cast.name_en) {
    errors.push({
      field: 'name',
      language: 'en',
      message: 'English name is required',
      code: 'REQUIRED_ENGLISH_NAME',
    });
  }

  return errors;
};

/**
 * Cast 지원 언어 목록 가져오기 함수
 */
export const getCastSupportedLanguages = (cast: CastFormData): LanguageCode[] => {
  const supportedLanguages: Set<LanguageCode> = new Set();

  if (cast.i18n_name) {
    Object.keys(cast.i18n_name).forEach(lang => {
      if (cast.i18n_name[lang as LanguageCode] && isValidLanguageCode(lang)) {
        supportedLanguages.add(lang as LanguageCode);
      }
    });
  }

  return Array.from(supportedLanguages);
};
// Season 타입 import (동적 import를 사용하여 순환 참조 방지)
type SeasonData = any;
type SeasonFormData = any;

/**
 * 레거시 Season 필드를 i18n 필드로 변환하는 함수
 */
export const convertSeasonLegacyToI18n = (season: SeasonData): SeasonFormData => {
  const result: SeasonFormData = {
    // 기본 필드들 복사
    id: season.id,
    series_id: season.series_id,
    season_number: season.season_number,
    slug: season.slug,
    status: season.status,
    created: season.created,
    modified: season.modified,
    dubbing_mode: season.dubbing_mode,
    season_order: season.season_order,
    age_rating: season.age_rating,
    release_date_start: season.release_date_start,
    release_date_end: season.release_date_end,
    cast: season.cast,
    genres: season.genres,
    series: season.series,
    contents: season.contents,
    categories: season.categories,
    is_deletable: season.is_deletable,

    // 레거시 필드 복사
    title: season.title || '',
    title_en: season.title_en,
    synopsis: season.synopsis,
    synopsis_en: season.synopsis_en,
    seo_title: season.seo_title,
    seo_synopsis: season.seo_synopsis,
    seo_keyword: season.seo_keyword,

    // i18n 필드 생성 및 변환
    i18n_title: {
      ko: season.i18n_title?.ko || season.title || '',
      en: season.i18n_title?.en || season.title_en,
      'zh-hans': season.i18n_title?.['zh-hans'],
      'zh-hant': season.i18n_title?.['zh-hant'],
    },
    i18n_synopsis: {
      ko: season.i18n_synopsis?.ko || season.synopsis,
      en: season.i18n_synopsis?.en || season.synopsis_en,
      'zh-hans': season.i18n_synopsis?.['zh-hans'],
      'zh-hant': season.i18n_synopsis?.['zh-hant'],
    },
    i18n_seo_title: {
      ko: season.i18n_seo_title?.ko || season.seo_title,
      en: season.i18n_seo_title?.en,
      'zh-hans': season.i18n_seo_title?.['zh-hans'],
      'zh-hant': season.i18n_seo_title?.['zh-hant'],
    },
    i18n_seo_synopsis: {
      ko: season.i18n_seo_synopsis?.ko || season.seo_synopsis,
      en: season.i18n_seo_synopsis?.en,
      'zh-hans': season.i18n_seo_synopsis?.['zh-hans'],
      'zh-hant': season.i18n_seo_synopsis?.['zh-hant'],
    },
    i18n_seo_keyword: {
      ko: season.i18n_seo_keyword?.ko || season.seo_keyword,
      en: season.i18n_seo_keyword?.en,
      'zh-hans': season.i18n_seo_keyword?.['zh-hans'],
      'zh-hant': season.i18n_seo_keyword?.['zh-hant'],
    },
  };

  return result;
};

/**
 * i18n Season 필드를 레거시 필드로 변환하는 함수
 */
export const convertSeasonI18nToLegacy = (season: SeasonFormData): SeasonData => {
  const result: SeasonData = {
    // 기본 필드들
    id: season.id,
    series_id: season.series_id,
    season_number: season.season_number,
    slug: season.slug,
    status: season.status,
    created: season.created,
    modified: season.modified,
    dubbing_mode: season.dubbing_mode,
    season_order: season.season_order,
    age_rating: season.age_rating,
    release_date_start: season.release_date_start,
    release_date_end: season.release_date_end,
    cast: season.cast,
    genres: season.genres,
    series: season.series,
    contents: season.contents,
    categories: season.categories,
    is_deletable: season.is_deletable,

    // i18n 필드에서 레거시 필드로 변환
    title: season.i18n_title?.ko || season.title || '',
    title_en: season.i18n_title?.en || season.title_en,
    synopsis: season.i18n_synopsis?.ko || season.synopsis,
    synopsis_en: season.i18n_synopsis?.en || season.synopsis_en,
    seo_title: season.i18n_seo_title?.ko || season.seo_title,
    seo_synopsis: season.i18n_seo_synopsis?.ko || season.seo_synopsis,
    seo_keyword: season.i18n_seo_keyword?.ko || season.seo_keyword,

    // i18n 필드
    i18n_title: season.i18n_title,
    i18n_synopsis: season.i18n_synopsis,
    i18n_seo_title: season.i18n_seo_title,
    i18n_seo_synopsis: season.i18n_seo_synopsis,
    i18n_seo_keyword: season.i18n_seo_keyword,
  };

  return result;
};

/**
 * Season 레거시 필드와 i18n 필드 간 실시간 동기화 함수
 */
export const syncSeasonFieldsToLegacy = (season: SeasonFormData): SeasonFormData => {
  return {
    ...season,
    // i18n 필드에서 레거시 필드로 동기화
    title: season.i18n_title?.ko || season.title || '',
    title_en: season.i18n_title?.en || season.title_en,
    synopsis: season.i18n_synopsis?.ko || season.synopsis,
    synopsis_en: season.i18n_synopsis?.en || season.synopsis_en,
    seo_title: season.i18n_seo_title?.ko || season.seo_title,
    seo_synopsis: season.i18n_seo_synopsis?.ko || season.seo_synopsis,
    seo_keyword: season.i18n_seo_keyword?.ko || season.seo_keyword,
  };
};

/**
 * Season 레거시 필드에서 i18n 필드로 동기화 함수
 */
export const syncSeasonLegacyFieldsToI18n = (season: SeasonFormData): SeasonFormData => {
  return {
    ...season,
    // 레거시 필드에서 i18n 필드로 동기화 (한국어로 설정)
    i18n_title: {
      ...season.i18n_title,
      ko: season.title || season.i18n_title?.ko,
      en: season.title_en || season.i18n_title?.en,
    },
    i18n_synopsis: {
      ...season.i18n_synopsis,
      ko: season.synopsis || season.i18n_synopsis?.ko,
      en: season.synopsis_en || season.i18n_synopsis?.en,
    },
    i18n_seo_title: {
      ...season.i18n_seo_title,
      ko: season.seo_title || season.i18n_seo_title?.ko,
    },
    i18n_seo_synopsis: {
      ...season.i18n_seo_synopsis,
      ko: season.seo_synopsis || season.i18n_seo_synopsis?.ko,
    },
    i18n_seo_keyword: {
      ...season.i18n_seo_keyword,
      ko: season.seo_keyword || season.i18n_seo_keyword?.ko,
    },
  };
};

/**
 * Season 필수 필드 검증 함수 (한국어 title, synopsis)
 */
export const validateSeasonRequiredFields = (season: SeasonFormData): I18nValidationError[] => {
  const errors: I18nValidationError[] = [];

  // 한국어 제목 필수 검증
  if (!season.i18n_title?.ko && !season.title) {
    errors.push({
      field: 'title',
      language: 'ko',
      message: 'Korean title is required',
      code: 'REQUIRED_KOREAN_TITLE',
    });
  }

  // 한국어 시놉시스 필수 검증 (요구사항에 따라 필수일 수 있음)
  if (!season.i18n_synopsis?.ko && !season.synopsis) {
    errors.push({
      field: 'synopsis',
      language: 'ko',
      message: 'Korean synopsis is required',
      code: 'REQUIRED_KOREAN_SYNOPSIS',
    });
  }

  return errors;
};

/**
 * Season 번역 완성도 계산 함수
 */
export const calculateSeasonTranslationCompleteness = (
  season: SeasonFormData,
  language: LanguageCode
): TranslationCompleteness => {
  const fields = ['title', 'synopsis', 'seo_title', 'seo_synopsis', 'seo_keyword'];
  let completedFields = 0;
  const totalFields = fields.length;

  fields.forEach(field => {
    const i18nFieldName = `i18n_${field}` as keyof SeasonFormData;
    const i18nField = season[i18nFieldName] as I18nFieldData;

    if (i18nField && i18nField[language]) {
      completedFields++;
    }
  });

  return {
    language,
    completedFields,
    totalFields,
    percentage: Math.round((completedFields / totalFields) * 100),
  };
};

/**
 * Season 지원 언어 목록 가져오기 함수
 */
export const getSeasonSupportedLanguages = (season: SeasonFormData): LanguageCode[] => {
  const supportedLanguages: Set<LanguageCode> = new Set();

  // 각 i18n 필드에서 실제 번역 데이터가 있는 언어 확인
  const i18nFields = [
    season.i18n_title,
    season.i18n_synopsis,
    season.i18n_seo_title,
    season.i18n_seo_synopsis,
    season.i18n_seo_keyword,
  ];

  i18nFields.forEach(field => {
    if (field) {
      Object.keys(field).forEach(lang => {
        if (field[lang as LanguageCode] && isValidLanguageCode(lang)) {
          supportedLanguages.add(lang as LanguageCode);
        }
      });
    }
  });

  return Array.from(supportedLanguages);
};

/**
 * Cast 모델을 위한 i18n 헬퍼 함수들
 * 요구사항 12.3, 12.4, 12.5: Cast 레거시 필드와 i18n 필드 간 실시간 동기화
 */

// Cast 타입 import (동적 import를 사용하여 순환 참조 방지)
import { CastData, CastFormData, CastApiRequest } from '../../types/i18n/i18nTypes';

/**
 * 레거시 Cast 필드를 i18n 필드로 변환하는 함수
 */
export const convertCastLegacyToI18n = (cast: CastData): CastFormData => {
  const result: CastFormData = {
    // 기본 필드들 복사
    id: cast.id,
    status: cast.status,
    created: cast.created,
    modified: cast.modified,
    birth_date: cast.birth_date,
    nationality: cast.nationality,
    thumbnail: cast.thumbnail,
    type: cast.type,
    slug: cast.slug,

    // 레거시 필드 복사
    name: cast.name || '',
    name_en: cast.name_en,

    // i18n 필드 생성 및 변환
    i18n_name: {
      ko: cast.i18n_name?.ko,
      en: cast.i18n_name?.en || cast.name_en,
      'zh-hans': cast.i18n_name?.['zh-hans'],
      'zh-hant': cast.i18n_name?.['zh-hant'],
    },
  };

  return result;
};

/**
 * i18n Cast 필드를 레거시 필드로 변환하는 함수
 */
export const convertCastI18nToLegacy = (cast: CastFormData): CastData => {
  const result: CastData = {
    // 기본 필드들
    id: cast.id || 0,
    status: cast.status || 'active',
    created: cast.created || new Date().toISOString(),
    modified: cast.modified || new Date().toISOString(),
    birth_date: cast.birth_date,
    nationality: cast.nationality,
    thumbnail: cast.thumbnail,
    type: cast.type,
    slug: cast.slug,

    // i18n 필드에서 레거시 필드로 변환
    name: cast.i18n_name?.ko || cast.name || '',
    name_en: cast.i18n_name?.en || cast.name_en,

    // i18n 필드
    i18n_name: cast.i18n_name,
  };

  return result;
};
